/**
 * Propiedades API module.
 *
 * Covers geographic lookups (countries, regions, comunas, sectores),
 * storage locations (bodegas, bodeguitas) and property CRUD.
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";
import { ApiResponse } from "../interfaces/api.interfaces";

// ─── Geo & location types ────────────────────────────────────────────────────

export interface GeoCountry {
  id: number;
  pais: string;
}

export interface GeoRegion {
  id: number;
  region: string;
  pais: number;
}

export interface GeoComuna {
  id: number;
  comuna: string;
  region: number;
}

export interface Sector {
  id: number;
  sector: string;
  comuna: number;
}

export interface Bodega {
  id: number;
  bodega: string;
  pais: number;
  region: number;
  comuna: number;
  sector: number;
}

export interface Bodeguita {
  id: number;
  bodeguita: string;
  id_propiedad: number;
  propiedad?: { id: number; nombrepropiedad: string } | null;
}

export interface Lavanderia {
  id: number;
  nombre: string;
  direccion?: string | null;
  descripcion?: string | null;
  activo: boolean;
}

// ─── Property types ───────────────────────────────────────────────────────────

/** Full property DTO as returned by the backend `toPropertyDto` mapper. */
export interface PropertyDto {
  id: number;
  idPropiedadCorto?: string | null;
  idPropiedadLargo?: string | null;
  nombrePropiedad: string;
  nombrePublicacion?: string | null;
  idCliente?: number | null;
  pais?: number | null;
  region?: number | null;
  comuna?: number | null;
  sector?: number | null;
  paisNombre?: string | null;
  regionNombre?: string | null;
  comunaNombre?: string | null;
  sectorNombre?: string | null;
  /** Backend may include additional dynamic fields. */
  [key: string]: any;
}

export interface PropertyListResponse {
  success: boolean;
  count: number;
  total: number;
  data: PropertyDto[];
}

// ─── propiedadesApi ───────────────────────────────────────────────────────────

export const propiedadesApi = {
  // ── CRUD ──────────────────────────────────────────────────────────────────

  /** GET /propiedades — paginated, filterable via query params.
   *
   * Sin `limit` explícito se pide el MÁXIMO del backend (400): casi todos
   * los llamadores sin params usan la lista como CATÁLOGO (selects de
   * propiedad en aseos, calendario, flujos, inventario, finanzas) y un
   * default más bajo truncaba silenciosamente las opciones (auditoría
   * 2026-07-13; tope subido a 400 por pedido del owner). Si el sistema
   * supera las 400 propiedades habilitadas, estos catálogos necesitarán
   * paginación iterativa o un endpoint sin tope. */
  getAll: (
    params?: Record<string, string | number | undefined>,
  ): Promise<PropertyListResponse | null> => {
    const filtered = params
      ? Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined),
        )
      : {};
    if (filtered.limit === undefined) filtered.limit = 400;
    const qs =
      "?" + new URLSearchParams(filtered as Record<string, string>).toString();
    return apiFetch<PropertyListResponse>(`/propiedades${qs}`, {
      errorMsg: "Error al obtener propiedades",
    });
  },

  /** GET /propiedades/filtered-properties */
  getAllFiltered: (
    params?: Record<string, string | number | undefined>,
  ): Promise<PropertyListResponse | null> => {
    const filtered = params
      ? Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined),
        )
      : {};
    const qs = Object.keys(filtered).length
      ? "?" + new URLSearchParams(filtered as Record<string, string>).toString()
      : "";
    return apiFetch<PropertyListResponse>(
      `/propiedades/filtered-properties${qs}`,
      { errorMsg: "Error al obtener propiedades" },
    );
  },

  /** GET /propiedades/propiedades/:id */
  getById: (id: number): Promise<ApiResponse<PropertyDto> | null> =>
    apiFetch<ApiResponse<PropertyDto>>(`/propiedades/propiedades/${id}`, {
      errorMsg: "Error al obtener la propiedad",
    }),

  /** GET /propiedades/cliente/:id */
  getByClient: (clientId: number): Promise<PropertyListResponse | null> =>
    apiFetch<PropertyListResponse>(`/propiedades/cliente/${clientId}`, {
      errorMsg: "Error al obtener propiedades del cliente",
    }),

  /** POST /propiedades */
  create: (
    body: Record<string, any>,
  ): Promise<ApiResponse<PropertyDto> | null> =>
    apiFetch<ApiResponse<PropertyDto>>("/propiedades", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Propiedad creada correctamente",
      errorMsg: "Error al crear la propiedad",
      // 409 de duplicado trae info accionable (qué propiedad choca). Modal
      // persistente en vez de toast para que el admin pueda leerlo.
      errorAsModal: true,
    }),

  /** PUT /propiedades/:id */
  update: (
    id: number,
    body: Record<string, any>,
  ): Promise<ApiResponse<PropertyDto> | null> =>
    apiFetch<ApiResponse<PropertyDto>>(`/propiedades/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Propiedad actualizada correctamente",
      errorMsg: "Error al actualizar la propiedad",
      errorAsModal: true,
    }),

  /**
   * GET /propiedades/:id/impacto-borrado — previo del borrado.
   *
   * Round 2026-08-06. Devuelve dos bloques:
   *   • `bloqueantes` — inventario (stock vivo + historial de compras que
   *     menciona la propiedad). Hay que resolver cada caso antes de borrar.
   *   • `cascada` — el resto, contado con los mismos predicados con los que
   *     después se borra.
   */
  getImpactoBorrado: (id: number): Promise<{ data: any } | null> =>
    apiFetch(`/propiedades/${id}/impacto-borrado`, {
      errorMsg: "Error al calcular el impacto del borrado",
    }),

  /**
   * DELETE /propiedades/:id — borrado físico con cascada total.
   *
   * Sin `successMsg` porque el toast lo arma el hook con el resumen de filas
   * eliminadas (`res.eliminados`).
   *
   * Round 2026-08-06 — ya no es incondicional: responde 409 si queda inventario
   * o historial de compras sin resolver. El body confirma explícitamente qué
   * hacer con cada caso (los ids salen del previo). El backend los recalcula y
   * NO confía en lo que llegue: si algo cambió en el medio, vuelve a 409.
   */
  delete: (
    id: number,
    confirmacion?: {
      /** ids del stock vivo que el admin acepta eliminar con la propiedad. */
      eliminarProductos?: number[];
      /** ids de las transacciones históricas que acepta dejar en pie. */
      conservarTransacciones?: number[];
    },
  ): Promise<{
    success: true;
    message: string;
    eliminados: Record<string, number>;
  } | null> =>
    apiFetch(`/propiedades/${id}`, {
      method: "DELETE",
      body: JSON.stringify({
        eliminarProductos: confirmacion?.eliminarProductos ?? [],
        conservarTransacciones: confirmacion?.conservarTransacciones ?? [],
      }),
      errorMsg: "Error al eliminar la propiedad",
      errorAsModal: true,
    }),

  /** PATCH /propiedades/:id/habilitado */
  toggleHabilitado: async (id: number, habilitado: boolean) => {
    const res = await apiFetch(`/propiedades/${id}/habilitado`, {
      method: "PATCH",
      body: JSON.stringify({ habilitado }),
    });
    return res;
  },

  /** GET /propiedades/nombres — all property names (lightweight, no pagination). */
  getNombres: (): Promise<{
    data: { id: number; nombrepropiedad: string }[];
  } | null> =>
    apiFetch<{ data: { id: number; nombrepropiedad: string }[] }>(
      "/propiedades/nombres",
      { errorMsg: "Error al obtener nombres de propiedades" },
    ),

  // ── Geo lookups ───────────────────────────────────────────────────────────

  /** GET /propiedades/countries */
  getCountries: (): Promise<{ data: GeoCountry[] } | null> =>
    apiFetch<{ data: GeoCountry[] }>("/propiedades/countries", {
      errorMsg: "Error al obtener países",
    }),

  /** GET /propiedades/regions[?countryId=N] */
  getRegions: (countryId?: number): Promise<{ data: GeoRegion[] } | null> => {
    const qs = countryId ? `?countryId=${countryId}` : "";
    return apiFetch<{ data: GeoRegion[] }>(`/propiedades/regions${qs}`, {
      errorMsg: "Error al obtener regiones",
    });
  },

  /** GET /propiedades/comunas[?regionId=N] */
  getComunas: (regionId?: number): Promise<{ data: GeoComuna[] } | null> => {
    const qs = regionId ? `?regionId=${regionId}` : "";
    return apiFetch<{ data: GeoComuna[] }>(`/propiedades/comunas${qs}`, {
      errorMsg: "Error al obtener comunas",
    });
  },

  /** GET /propiedades/sectores */
  getSectores: (): Promise<{ data: Sector[] } | null> =>
    apiFetch<{ data: Sector[] }>("/propiedades/sectores", {
      errorMsg: "Error al obtener sectores",
    }),

  // ── Geo CRUD ──────────────────────────────────────────────────────────────

  createCountry: (pais: string) =>
    apiFetch<{ data: GeoCountry }>("/propiedades/countries", {
      method: "POST",
      body: JSON.stringify({ pais }),
      successMsg: "País creado",
    }),
  updateCountry: (id: number, pais: string) =>
    apiFetch<{ data: GeoCountry }>(`/propiedades/countries/${id}`, {
      method: "PUT",
      body: JSON.stringify({ pais }),
      successMsg: "País actualizado",
    }),
  deleteCountry: (id: number) =>
    apiFetch(`/propiedades/countries/${id}`, {
      method: "DELETE",
      successMsg: "País eliminado",
    }),

  createRegion: (region: string, pais: number) =>
    apiFetch<{ data: GeoRegion }>("/propiedades/regions", {
      method: "POST",
      body: JSON.stringify({ region, pais }),
      successMsg: "Región creada",
    }),
  updateRegion: (id: number, region: string, pais?: number) =>
    apiFetch<{ data: GeoRegion }>(`/propiedades/regions/${id}`, {
      method: "PUT",
      body: JSON.stringify({ region, ...(pais != null && { pais }) }),
      successMsg: "Región actualizada",
    }),
  deleteRegion: (id: number) =>
    apiFetch(`/propiedades/regions/${id}`, {
      method: "DELETE",
      successMsg: "Región eliminada",
    }),

  createComuna: (comuna: string, region: number) =>
    apiFetch<{ data: GeoComuna }>("/propiedades/comunas", {
      method: "POST",
      body: JSON.stringify({ comuna, region }),
      successMsg: "Comuna creada",
    }),
  updateComuna: (id: number, comuna: string, region?: number) =>
    apiFetch<{ data: GeoComuna }>(`/propiedades/comunas/${id}`, {
      method: "PUT",
      body: JSON.stringify({ comuna, ...(region != null && { region }) }),
      successMsg: "Comuna actualizada",
    }),
  deleteComuna: (id: number) =>
    apiFetch(`/propiedades/comunas/${id}`, {
      method: "DELETE",
      successMsg: "Comuna eliminada",
    }),

  createSector: (sector: string, comuna: number) =>
    apiFetch<{ data: Sector }>("/propiedades/sectores", {
      method: "POST",
      body: JSON.stringify({ sector, comuna }),
      successMsg: "Sector creado",
    }),
  updateSector: (id: number, sector: string, comuna?: number) =>
    apiFetch<{ data: Sector }>(`/propiedades/sectores/${id}`, {
      method: "PUT",
      body: JSON.stringify({ sector, ...(comuna != null && { comuna }) }),
      successMsg: "Sector actualizado",
    }),
  deleteSector: (id: number) =>
    apiFetch(`/propiedades/sectores/${id}`, {
      method: "DELETE",
      successMsg: "Sector eliminado",
    }),

  // ── Lavanderías ──────────────────────────────────────────────────────────

  /** GET /finanzas/empresa/lavanderias */
  getLavanderias: (): Promise<{ data: Lavanderia[] } | null> =>
    apiFetch<{ data: Lavanderia[] }>("/finanzas/empresa/lavanderias", {
      errorMsg: "Error al obtener lavanderías",
    }),

  // ── Storage ───────────────────────────────────────────────────────────────

  /** GET /propiedades/bodegas */
  getBodegas: (
    params?: Record<string, string | number | undefined>,
  ): Promise<{ data: Bodega[] } | null> => {
    const filtered = params
      ? Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined),
        )
      : {};
    const qs = Object.keys(filtered).length
      ? "?" + new URLSearchParams(filtered as Record<string, string>).toString()
      : "";
    return apiFetch<{ data: Bodega[] }>(`/propiedades/bodegas${qs}`, {
      errorMsg: "Error al obtener bodegas",
    });
  },

  /** GET /propiedades/bodeguitas */
  getBodeguitas: (
    params?: Record<string, string | number | undefined>,
  ): Promise<{ data: Bodeguita[] } | null> => {
    const filtered = params
      ? Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined),
        )
      : {};
    const qs = Object.keys(filtered).length
      ? "?" + new URLSearchParams(filtered as Record<string, string>).toString()
      : "";
    return apiFetch<{ data: Bodeguita[] }>(`/propiedades/bodeguitas${qs}`, {
      errorMsg: "Error al obtener bodeguitas",
    });
  },

  /** POST /propiedades/bodeguitas/rellenar */
  rellenarBodeguitas: (): Promise<{ message: string; creadas: number } | null> =>
    apiFetch<{ message: string; creadas: number }>("/propiedades/bodeguitas/rellenar", {
      method: "POST",
      errorMsg: "Error al rellenar bodeguitas",
    }),

  createBodeguita: (body: {
    bodeguita: string;
    id_propiedad: number;
  }): Promise<{ success: boolean; data: Bodeguita } | null> =>
    apiFetch<{ success: boolean; data: Bodeguita }>("/propiedades/bodeguitas", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Bodeguita creada correctamente",
      errorMsg: "Error al crear la bodeguita",
    }),

  updateBodeguita: (
    id: number,
    body: { bodeguita: string; id_propiedad: number },
  ): Promise<{ success: boolean; data: Bodeguita } | null> =>
    apiFetch<{ success: boolean; data: Bodeguita }>(
      `/propiedades/bodeguitas/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(body),
        successMsg: "Bodeguita actualizada correctamente",
        errorMsg: "Error al actualizar la bodeguita",
      },
    ),

  deleteBodeguita: (id: number): Promise<any | null> =>
    apiFetch(`/propiedades/bodeguitas/${id}`, {
      method: "DELETE",
      successMsg: "Bodeguita eliminada correctamente",
      errorMsg: "Error al eliminar la bodeguita",
    }),

  createBodega: (
    body: Omit<Bodega, "id">,
  ): Promise<{ success: boolean; data: Bodega } | null> =>
    apiFetch<{ success: boolean; data: Bodega }>("/propiedades/bodegas", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Bodega creada correctamente",
      errorMsg: "Error al crear la bodega",
    }),

  updateBodega: (
    id: number,
    body: Partial<Omit<Bodega, "id">>,
  ): Promise<{ success: boolean; data: Bodega } | null> =>
    apiFetch<{ success: boolean; data: Bodega }>(`/propiedades/bodegas/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Bodega actualizada correctamente",
      errorMsg: "Error al actualizar la bodega",
    }),

  deleteBodega: (id: number): Promise<any | null> =>
    apiFetch(`/propiedades/bodegas/${id}`, {
      method: "DELETE",
      successMsg: "Bodega eliminada correctamente",
      errorMsg: "Error al eliminar la bodega",
    }),

  // ── Información para el huésped — adjuntos multimedia ───────────────────
  //
  // Endpoints servidos por propertyGuestFiles.controller.js. Misma
  // semántica que /api/contratos/:id/archivos pero para la columna
  // `propiedades.info_huesped_archivos`. Permite subir fotos, videos,
  // PDFs y documentos que acompañan las instrucciones escritas
  // (mensaje_inicial, clave_wifi, clave_cerradura, otros) de cada
  // propiedad. URLs vienen firmadas (JWT en path) con TTL 6h.
  // Ver docs/modulo-propiedades-info-huesped.md.

  /** GET /propiedades/:id/info-huesped/archivos */
  getInfoHuespedArchivos: (
    propiedadId: number,
  ): Promise<{ success: boolean; data: PropertyInfoHuespedFile[] } | null> =>
    apiFetch<{ success: boolean; data: PropertyInfoHuespedFile[] }>(
      `/propiedades/${propiedadId}/info-huesped/archivos`,
      { errorMsg: "Error al cargar los archivos para el huésped" },
    ),

  /**
   * POST /propiedades/:id/info-huesped/archivos — sube un archivo (FormData
   * multipart, campo "file"). Devuelve el metadato con URLs firmadas listas
   * para renderizar. NO mostramos `successMsg` aquí — el componente
   * GuestFilesManager arma su propio toast con el nombre del archivo para
   * que el admin sepa exactamente cuál subió cuando arrastra varios.
   */
  uploadInfoHuespedArchivo: async (
    propiedadId: number,
    file: File,
  ): Promise<PropertyInfoHuespedFile | null> => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await apiFetch<{
      success: boolean;
      data: PropertyInfoHuespedFile;
    }>(`/propiedades/${propiedadId}/info-huesped/archivos`, {
      method: "POST",
      body: formData,
      errorMsg: "Error al subir el archivo",
    });
    return res?.data ?? null;
  },

  /**
   * DELETE /propiedades/:id/info-huesped/archivos?key=<key>
   * Devuelve la lista actualizada (con URLs re-firmadas) para que el
   * componente sincronice el estado sin un segundo GET.
   */
  deleteInfoHuespedArchivo: async (
    propiedadId: number,
    key: string,
  ): Promise<PropertyInfoHuespedFile[]> => {
    const res = await apiFetch<{
      success: boolean;
      data: PropertyInfoHuespedFile[];
    }>(
      `/propiedades/${propiedadId}/info-huesped/archivos?key=${encodeURIComponent(key)}`,
      {
        method: "DELETE",
        successMsg: "Archivo eliminado",
        errorMsg: "Error al eliminar el archivo",
      },
    );
    return res?.data ?? [];
  },
};

// ─── Tipos del módulo Info Huésped ─────────────────────────────────────────
//
// `PropertyInfoHuespedFile` es el FileEntry que devuelve
// storageService.attachUrls() — un metadato del archivo con URLs firmadas
// listas para usar en <img>/<video>/iframe. Los campos `url` y `thumbUrl`
// se generan al leer (no se persisten en DB) y caducan en 6h. El componente
// GuestFilesManager re-fetcha en cada apertura del modal para evitar URLs
// expiradas.

export interface PropertyInfoHuespedFile {
  provider: "local";
  key: string;
  thumbKey: string | null;
  /** URL firmada absoluta (full size). Caduca según FILE_URL_TTL_SEC. */
  url: string;
  /** URL firmada del thumbnail (solo imágenes/videos). */
  thumbUrl: string | null;
  originalName: string;
  mimeType: string;
  size: number;
  /** Tipo lógico — el frontend lo usa para decidir cómo renderizar. */
  kind: "image" | "video" | "doc";
  /** URL firmada de la versión liviana del video (480p); `null` si no existe en disco. */
  previewUrl?: string | null;
  /** Peso (bytes) de la versión liviana, si existe. */
  previewSize?: number | null;
}

/**
 * Helper para abrir un archivo en una pestaña nueva con
 * Content-Disposition: attachment forzado vía `?d=1`. Útil para descargar
 * docs/PDFs grandes sin tener que fetchear el blob completo al frontend.
 * Se usa como alternativa al helper `downloadFile` (que blob-eando puede
 * disparar OOM en videos pesados).
 */
export function buildDownloadUrl(file: PropertyInfoHuespedFile): string {
  // Concatena `?d=1` o `&d=1` según ya haya querystring.
  const sep = file.url.includes("?") ? "&" : "?";
  return `${file.url}${sep}d=1`;
}

